import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type DayPerson } from '../lib/api.js';
import { PickLegend } from './WorksheetEditors.js';

/**
 * Who was in a day — on the entry that named them, and on the day.
 *
 * Release 2 of 2.0 (docs/PRD-2.0.md §4). Worked exactly like an entry's
 * subjects and a worksheet's traps: the app's picks dashed, his solid, one
 * tap takes one off, a name he types is his. Unlike the subjects there is no
 * Save: a person is one row, and removing or adding one is the whole edit.
 *
 * Shown for the DAY, not only this entry: two entries on one day that both
 * name Hans are one fact, and the row says who was in the day. The entry that
 * filed a pick is remembered on the row (voiceLogId) for the person page.
 */
export function DayPeopleRow({ day, voiceLogId }: { day: string; voiceLogId: string | null }): JSX.Element | null {
  const [people, setPeople] = useState<DayPerson[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.dayPeople(day).then(setPeople).catch(() => setPeople([]));
  }, [day]);

  if (people === null) return null;
  if (people.length === 0 && !editing) {
    return (
      <div className="stack-sm stack">
        <button type="button" className="chips-change" onClick={() => setEditing(true)}>
          Who was in this day?
        </button>
      </div>
    );
  }

  const anyApp = people.some((p) => p.source === 'app');

  async function remove(personId: string): Promise<void> {
    setBusy(true);
    try {
      setPeople(await api.removeDayPerson(day, personId));
    } finally {
      setBusy(false);
    }
  }
  async function add(): Promise<void> {
    const name = typed.trim();
    if (!name) return;
    setBusy(true);
    try {
      setPeople(await api.addDayPerson(day, name, voiceLogId));
      setTyped('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack-sm stack day-people">
      <div className="row-between">
        <span className="faint">With</span>
        {!editing && (
          <button type="button" className="chips-change" onClick={() => setEditing(true)}>
            Change
          </button>
        )}
      </div>
      {anyApp && editing && <PickLegend appLabel="the app’s pick" note="Tap one to take it off." />}
      <div className="chips">
        {people.map((p) =>
          editing ? (
            <button
              key={p.personId}
              type="button"
              className={p.source === 'app' ? 'chip chip-app' : 'chip'}
              aria-label={`${p.name}${p.source === 'app' ? " (the app's pick)" : ''} — take off`}
              disabled={busy}
              onClick={() => void remove(p.personId)}
            >
              {p.name} ×
            </button>
          ) : (
            <Link key={p.personId} className={p.source === 'app' ? 'chip chip-app' : 'chip'} to={`/people/${p.personId}`}>
              {p.name}
            </Link>
          ),
        )}
      </div>
      {editing && (
        <>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <label htmlFor={`add-person-${day}`} className="sr-only">
              Someone else who was in this day
            </label>
            <input
              id={`add-person-${day}`}
              className="input"
              style={{ flex: 1, minWidth: 0 }}
              value={typed}
              maxLength={40}
              placeholder="Someone else, by name"
              disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void add();
                }
              }}
            />
            <button type="button" className="btn btn-secondary" onClick={() => void add()} disabled={busy || !typed.trim()}>
              Add
            </button>
          </div>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setEditing(false)} disabled={busy}>
            Done
          </button>
        </>
      )}
    </div>
  );
}
